import axios from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../store/authStore";
import { rememberReturnTo } from "../lib/returnTo";
import { deviceIdHeaders } from "../lib/deviceId";
import { useConsentStore, type AuthConsentFlags } from "../store/consentStore";

/** Sign-in page query that shows the Under 14 screen (the refresh was refused with UNDER_AGE). */
export const UNDER14_QUERY = "under14";

/**
 * Pages a signed-out user may be on. A failed refresh there must not navigate: these pages call public routes
 * (e.g. GET /consent from the storage banner), and a redirect to /login would reload the page, call again, fail
 * again — an endless reload while a dead session's cookie is still sent.
 */
const PUBLIC_PATHS = ["/login", "/auth/otp", "/auth/set-password", "/forgot-password", "/auth/google/callback"];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// One refresh at a time across every client: concurrent 401s from main-api
// and stc-chat queue behind the same refresh-token call.
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(err: unknown) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else resolve();
  });
  failedQueue = [];
}

/** On a 401, refresh the access token once and retry the request. */
export function attachAuthRefresh(instance: AxiosInstance): void {
  instance.interceptors.response.use(undefined, async (error) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => instance(original));
    }
    original._retry = true;
    isRefreshing = true;
    try {
      const res = await axios.post<AuthConsentFlags>("/iam/api/v1/auth/refresh-token", null, {
        withCredentials: true,
        headers: deviceIdHeaders(),
      });
      // The refresh carries the gate flags too (e.g. a new material version published since sign-in).
      if (res?.data && typeof res.data === "object") useConsentStore.getState().setAuthFlags(res.data);
      isRefreshing = false;
      processQueue(null);
      return instance(original);
    } catch (err) {
      isRefreshing = false;
      processQueue(err);
      const refused = err as { response?: { status?: number; data?: { code?: string } } };
      // 503: the age check was unavailable — no new token, but the session is not gone either: the request fails
      // and the user tries again (never treated as a success, never as a sign-out).
      if (refused.response?.status === 503) return Promise.reject(err);
      const underAge = refused.response?.data?.code === "UNDER_AGE";
      // The session is gone: the next sign-in must register for pushes again (PUT /api/push/device),
      // so drop this account's FCM marker before the user id is cleared.
      const { userId, clearAuth } = useAuthStore.getState();
      if (userId) localStorage.removeItem(`fcm_token_${userId}`);
      clearAuth();
      if (typeof window !== "undefined") {
        const { pathname, search, hash } = window.location;
        if (isPublicPath(pathname)) {
          // Already where a signed-out user belongs: stay. Ask iam to expire the dead cookies (idempotent, 204)
          // so the next public call goes out anonymous; a read is retried once without them.
          await axios
            .post("/iam/api/v1/auth/sign-out", null, { withCredentials: true, headers: deviceIdHeaders() })
            .catch(() => undefined);
          if (underAge && !new URLSearchParams(search).has(UNDER14_QUERY)) {
            window.location.href = `/login?${UNDER14_QUERY}=1`;
          } else if ((original.method ?? "get").toLowerCase() === "get") {
            return instance(original);
          }
          return Promise.reject(err);
        }
        if (underAge) {
          // The account is under 14: signed out by the server; the sign-in page shows Under 14.
          window.location.href = `/login?${UNDER14_QUERY}=1`;
        } else {
          rememberReturnTo(pathname + search + hash);
          window.location.href = "/login";
        }
      }
      return Promise.reject(err);
    }
  });
}
